import {
  DashboardDefinition,
  definitionsDifferInLayout,
  normalizeDefinition,
} from "../DashboardHeader/DashboardParams";

/**
 * What to do with a localStorage draft when the dashboard seeds a context
 * (org / scope / dashboard id):
 *
 * - `"seed-baseline"` — no draft exists; seed the baseline (saved record or
 *   default layout), exactly the draft-less behavior.
 * - `"drop-draft"` — a draft exists but holds no real LAYOUT edit: it
 *   normalizes to the baseline's sections + cards (a params-only `range`/
 *   `period` drift counts as no edit — the URL carries the user's params).
 *   Clear it and seed the baseline.
 * - `"apply-draft"` — the draft differs from the baseline in layout, so the
 *   DRAFT is what seeds, params and all. The draft is a working copy, not a
 *   proposal: it is applied without asking, because asking on every return to
 *   an edited dashboard was the interruption this design set out to remove.
 */
export type DraftSeedPlan =
  | { kind: "seed-baseline" }
  | { kind: "drop-draft" }
  | { kind: "apply-draft"; draft: DashboardDefinition };

/**
 * Decide how a stored draft participates in seeding, given the baseline that
 * would load without it (the saved record's definition, or the default layout
 * for an unsaved view). The decision compares LAYOUT only
 * ({@link definitionsDifferInLayout}): a draft or record compares on meaning,
 * not stored bytes, and a params-only difference is not an edit worth keeping
 * a draft alive for.
 */
export function planDraftSeed(
  draft: DashboardDefinition | null,
  baseline: DashboardDefinition,
): DraftSeedPlan {
  if (draft === null) {
    return { kind: "seed-baseline" };
  }
  const normalizedDraft = normalizeDefinition(draft);
  if (!definitionsDifferInLayout(normalizedDraft, baseline)) {
    return { kind: "drop-draft" };
  }
  return { kind: "apply-draft", draft: normalizedDraft };
}
